<template>
  <div class="w-full h-full flex flex-col" v-if="currentProject">
    <div class="mt-165p lg:pb-112p">
      <h1 class="sr-only">{{ currentProject.brand }}</h1>
      <div class="flex justify-center items-center h-265p shadow-4xl z-20 mb-20" data-aos="fade-up">
        <img :src="currentProject.image" :alt="`${currentProject.brand} logo`" class="w-367p">
      </div>
    </div>
    <div class="px-6 sm:px-50p flex flex-col lg:flex-row gap-12">
      <div class="flex-2 lg:pr-20" data-aos="fade-up">
        <h2 class="text-24 font-bold text-navy">
          Brief
        </h2>
        <!-- текст из админки выводим как текст: вставь его разметкой — и через
             описание проекта на страницу попадёт чужой скрипт. Вёрстку
             допускаем только у описаний, лежащих в самом репозитории -->
        <div
          v-if="currentProject.descriptionIsMarkup"
          class="leading-6 mt-8 text-neutral/500"
          v-html="currentProject.description"
        />
        <div v-else class="leading-6 mt-8 text-neutral/500 whitespace-pre-line">
          {{ currentProject.description }}
        </div>
      </div>
      <div class="flex-1 w-full lg:min-w-350p lg:px-6" data-aos="fade-up" data-aos-delay="120">
        <h2 class="text-24 font-bold text-navy">
          Technologies
        </h2>
        <div class="flex flex-wrap gap-5 mt-11">
          <span
            v-for="item in currentProject.technologies"
            :key="item"
            class="border border-primary1 rounded-45p px-8 py-1"
          >
            {{ item }}
          </span>
        </div>
      </div>
    </div>

    <Carousel v-if="currentProject.gallery" :settings="carouselSettings" class="cursor-pointer mb-100p">
      <Slide v-for="photo in currentProject.gallery" :key="photo.id" class="py-50p">
        <div class="carousel__item gap-24">
          <div
            class="background-screen bg-no-repeat bg-cover"
            :style="{ backgroundImage: `url(${photo.url})` }"
          />
        </div>
      </Slide>
    </Carousel>

    <Carousel v-else :settings="carouselSettings" class="cursor-pointer mb-100p">
      <Slide v-for="(photo, index) in currentProject.sliderPhotos" :key="index" class="py-50p">
        <div class="carousel__item gap-24">
          <div :class="`bg-${photo}`" class="background-screen bg-no-repeat bg-cover"/>
        </div>
      </Slide>
    </Carousel>

    <div v-if="currentProject.keyResults" class="px-6 sm:px-50p mb-128p">
      <h2 class="text-center text-navy text-24 font-bold" data-aos="fade-up">
        Key Results
      </h2>
      <div class="mt-20 flex flex-wrap gap-8 lg:gap-12 justify-center">
        <div
          v-for="(item, index) in currentProject.keyResults"
          :key="item"
          class="flex flex-col justify-center items-center rounded-45p shadow-4xl py-8 px-8 sm:px-12 w-full max-w-400p"
          data-aos="fade-up"
          :data-aos-delay="(index % 3) * 90"
        >
          <img src="@/assets/icons/keyResultsIcon.svg" alt="" class="mt-4 relative">
          <p class="text-18 text-navy text-center mt-8 leading-6">
            {{ item }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue3-carousel/dist/carousel.css'

export default {
  name: 'ProjectInfo',
}
</script>

<script setup>
import { reactive, computed, onMounted, watch } from 'vue'
import { useContent } from '@/composables/useContent'
import { useRouter, useRoute } from 'vue-router'
import projectsData from '@/store/projects.js'

import { Carousel, Slide } from 'vue3-carousel'

const route = useRoute()
const router = useRouter()

const carouselSettings = reactive({
    itemsToShow: '1.5',
    wrapAround: true,
    autoplay: '4000',
    breakpoints: {
      1536: {
        itemsToShow: '3.95'
      },
      1440: {
        itemsToShow: '2.95'
      },
      1024: {
        itemsToShow: '2.35'
      },
      768: {
        itemsToShow: '1.8'
      },
      640: {
        itemsToShow: '1.5'
      },
      500: {
        itemsToShow: '1.6'
      },
      460: {
        itemsToShow: '1.4'
      },
    }
  })
// В адресе теперь имя проекта, а не порядковый номер: он не меняется
// при правках в админке. Старые ссылки с номером тоже находят проект.
const slugify = (value) => String(value ?? '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '')

const { items: apiProjects } = useContent('project', [])

const currentProject = computed(() => {
  const key = String(route.params.id ?? '')

  // галерея, технологии и подробное описание пока лежат в вёрстке
  const local = projectsData.find(
    (el) => slugify(el.brand) === key || String(el.id) === key
  )

  const remote = apiProjects.value.find(
    (item) => (item.slug || slugify(item.name)) === key
  )

  if (!local && !remote) return null

  return {
    ...(local ?? {}),
    // изображения, загруженные через админку, заменяют галерею из вёрстки
    gallery: remote?.gallery?.length ? remote.gallery : null,
    brand: remote?.name ?? local?.brand,
    image: remote?.file?.url || local?.image,
    'short-description': remote?.brief ?? local?.['short-description'],
    description: remote?.description || local?.description,
    // разметку разрешаем только тексту из репозитория
    descriptionIsMarkup: !remote?.description && Boolean(local?.description),
    // в админке технологии перечисляют через запятую
    technologies: remote?.technologies
      ? remote.technologies.split(',').map((item) => item.trim()).filter(Boolean)
      : local?.technologies,
    keyResults: [remote?.key_result1, remote?.key_result2, remote?.key_result3]
      .filter(Boolean)
      .length
      ? [remote.key_result1, remote.key_result2, remote.key_result3].filter(Boolean)
      : local?.keyResults,
  }
})

const applyProject = () => {
  if (currentProject.value) {
    document.title = route.meta.title + ' | ' + currentProject.value.brand
  }
}

// решение о переходе на 404 принимаем только после ответа API,
// иначе страница отбрасывала бы посетителя ещё до загрузки данных
watch(apiProjects, () => {
  if (!currentProject.value) {
    router.push('/pageNotFound')

    return
  }

  applyProject()
})

onMounted(applyProject)
</script>

<style scoped>
.background-screen {
  width: 367px;
  height: 202px;
}
.carousel__slide {
  padding: 100px 10px
}
.carousel__slide > .carousel__item {
  transform: scale(1);
  opacity: 0.5;
  transition: 0.5s;
  padding: 68px 38px;
}
.carousel__slide--visible > .carousel__item {
  opacity: 0.5;
  transform: rotateY(0);
}
.carousel__slide--next > .carousel__item {
  opacity: 0.5;
}
.carousel__slide--prev > .carousel__item {
  opacity: 0.5;
}
.carousel__slide--active > .carousel__item {
  position: relative;
  opacity: 1;
  transform: scale(1.1);
}
.carousel__slide--active > .carousel__item:after {
  content: '';
  position: absolute;
  transform: scale(0.9);
  top: 29px;
  left: 0;
  right: 0;
  bottom: 0;
  content: '';
  width: 100%;
  height: 120%;
  background-image: url("../assets/carousel_wrapper_image.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  z-index: -1;
}
@media (max-width: 640px) {
  .background-screen {
    width: 206px;
    height: 115px;
  }
  .carousel__slide > .carousel__item {
    padding: 39px 22px;
  }
  .carousel__slide--active > .carousel__item:after {
    top: 16px;
  }
}
</style>
